import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardBody, Button, Table, Badge, Container, Row, Col } from 'react-bootstrap';
import Breadcrumbs from '../../../../Common/Breadcrumbs';
import { DeleteModal } from '../../../../Common/DeleteModal';
import ShimmerLoader from '../../../../Common/ShimmerLoader';
import '../../../../components/PlayerComponents.css';
import '../ClubDetails.css';

import PlayerForm from '../../../../components/PlayerForm';
import {
  addPlayer,
  updatePlayer,
  deletePlayer,
  getPlayersByTeamId,
  getPlayerById,
  Player, // API Player interface
} from '../../../../api/club/playerService';
import { PlayerFormData } from '../../../../types/Player';
import { getAgeCategoriesFromTournaments, getTeamById } from '../../../../api/club/teamService';
import { getImageUrl } from '../../../../utils/imageUrl';

interface Team {
  _id: string;
  name: string;
  shortName: string;
  logo: string;
  ageGroups: string[];
  coachManagerName: string;
  contactDetails: string;
  players: number;
  verifiedPlayers?: number;
  paidPlayers?: number;
  sports?: string;
  status?: string;
}

interface ClubPlayerManagementProps {
  teamId?: string;
  clubId?: string;
  breadcrumbItems?: Array<{ label: string; path?: string }>;
}

const ClubPlayerManagement: React.FC<ClubPlayerManagementProps> = ({
  teamId: propTeamId,
  clubId: propClubId,
  breadcrumbItems = []
}) => {
  const { teamId: urlTeamId, clubId: urlClubId } = useParams<{ teamId: string; clubId: string }>();
  const navigate = useNavigate();
  
  // Use props if provided, otherwise use URL params
  const teamId = propTeamId || urlTeamId || '';
  const clubId = propClubId || urlClubId || '';
  
  const [team, setTeam] = useState<Team | null>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [showPlayerForm, setShowPlayerForm] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState<Player | null>(null);
  const [ageCategories, setAgeCategories] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Delete modal state
  const [showDeletePlayerModal, setShowDeletePlayerModal] = useState(false);
  const [deletePlayerTarget, setDeletePlayerTarget] = useState<Player | null>(null);
  const [deletePlayerError, setDeletePlayerError] = useState<string | null>(null);
  const [isDeletingPlayer, setIsDeletingPlayer] = useState(false);

  // Load team data
  const loadTeamData = async () => {
    try {
      setLoading(true);
      const teamData = await getTeamById(teamId);
      
      // Map TeamDetails to Team interface
      const mappedTeam: Team = {
        _id: teamData._id,
        name: teamData.name,
        shortName: teamData.shortName,
        logo: teamData.logo,
        ageGroups: Array.isArray(teamData.ageCategory) ? teamData.ageCategory : [teamData.ageCategory],
        coachManagerName: teamData.managerName,
        contactDetails: teamData.managerContactNum.toString(),
        players: 0, // Will be updated when players are loaded
        sports: 'Football',
        status: teamData.isActive ? 'active' : 'inactive'
      };
      
      setTeam(mappedTeam);
    } catch (error) {
      console.error('Failed to load team data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load players data
  const loadPlayers = async () => {
    try {
      const playersData = await getPlayersByTeamId(teamId);
      setPlayers(playersData);
      
      // Update team players count
      if (team) {
        setTeam(prev => prev ? { ...prev, players: playersData.length } : null);
      }
    } catch (error) {
      console.error('Failed to load players:', error);
      setPlayers([]);
    }
  };

  // Load age categories - All Clubs specific list
  const loadAgeCategories = async () => {
    // All Clubs module specific age categories
    const allClubsAgeCategories = [
      'U8(Co-Ed)', 
      'U10(Co-Ed)', 
      'U12(Co-Ed)', 
      'Sub Junior Boys', 
      'Junior Boys', 
      'Elite Boys', 
      'U14 Girls', 
      'U17 Girls'
    ];
    setAgeCategories(allClubsAgeCategories);
  };

  // Initialize data
  useEffect(() => {
    if (teamId) {
      loadTeamData();
      loadPlayers();
      loadAgeCategories();
    }
  }, [teamId]);



  // Handle edit player
  const handleEditPlayer = async (player: Player) => {
    try {
      const playerData = await getPlayerById(player._id);
      setEditingPlayer(playerData);
      setShowPlayerForm(true);
    } catch (error) {
      console.error('Failed to fetch player data for editing:', error);
      setEditingPlayer(null);
      setShowPlayerForm(false);
    }
  };

  // Handle delete player
  const handleDeletePlayer = (player: Player) => {
    setDeletePlayerTarget(player);
    setShowDeletePlayerModal(true);
  };

  // Confirm delete player
  const confirmDeletePlayer = async () => {
    if (!deletePlayerTarget) return;

    try {
      setIsDeletingPlayer(true);
      setDeletePlayerError(null);
      await deletePlayer(deletePlayerTarget._id);
      setShowDeletePlayerModal(false);
      setDeletePlayerTarget(null);
      await loadPlayers();
    } catch (error: any) {
      console.error('Failed to delete player:', error);
      setDeletePlayerError(error.message || 'Failed to delete player. Please try again.');
    } finally {
      setIsDeletingPlayer(false);
    }
  };

  // Close delete modal
  const closeDeleteModal = () => {
    setShowDeletePlayerModal(false);
    setDeletePlayerTarget(null);
    setDeletePlayerError(null);
  };

  // Handle close player form
  const handleClosePlayerForm = () => {
    setShowPlayerForm(false);
    setEditingPlayer(null);
    setSubmitError(null);
  };

  // Default breadcrumb items
  const defaultBreadcrumbItems = [
    { label: 'All Clubs', path: '/all-clubs' },
    { label: 'Club Details', path: `/all-clubs/${clubId}` },
    { label: 'Player Management' }
  ];

  const finalBreadcrumbItems = breadcrumbItems.length > 0 ? breadcrumbItems : defaultBreadcrumbItems;

  if (loading) {
    return (
      <Container fluid>
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs items={finalBreadcrumbItems} />
        </div>
        <ShimmerLoader type="table-rows" variant="player" rows={5} />
      </Container>
    );
  }

  if (!teamId) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <h5>Team ID not found</h5>
          <p className="text-muted">Please select a valid team.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="club-player-management-container" style={{ marginTop: '20px' }}>
      <Container fluid>
        {/* Breadcrumbs */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs items={finalBreadcrumbItems} />
        </div>

        {/* Header */}
        <div className="page-header-section mb-4" style={{ padding: '0 15px' }}>
          <div className="d-flex align-items-center" style={{ gap: '16px', marginBottom: '24px' }}>
            <button 
              className="back-button"
              onClick={() => navigate(`/all-clubs/${clubId}`)}
            >
              <i className="ri-arrow-left-line"></i>
            </button>
            <div className="team-logo-small" style={{ flexShrink: 0 }}>
              {team?.logo ? (
                <img 
                  src={getImageUrl(team.logo)}
                  alt={team.name}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              ) : (
                <div className="default-team-logo">
                  <i className="ri-team-line"></i>
                </div>
              )}
            </div>
            <div className="d-flex align-items-center" style={{ gap: '8px' }}>
              <h1 className="page-title mb-0">{team?.name || 'Team'}</h1>
              {team?.ageGroups && team.ageGroups.length > 0 && (
                <Badge bg="success" className="age-category-badge">
                  {team.ageGroups[0]}
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Players Section */}
        <div className="teams-section-container">
          {/* Players Header Section - Same styling as TeamsHeader */}
          <div className="teams-header-section mb-4" style={{ position: 'relative', zIndex: 10, padding: '0 15px', background: 'transparent', margin: '0', backgroundColor: 'transparent' }}>
            <div className="d-flex justify-content-between align-items-center" style={{ position: 'relative', zIndex: 10, width: '100%', padding: '0', background: 'transparent', backgroundColor: 'transparent' }}>
              {/* Left: Players Title and Description */}
              <div className="teams-section-info" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: '0 0 auto', background: 'transparent', backgroundColor: 'transparent' }}>
                <h4 className="section-title mb-2" style={{ fontSize: '20px', fontWeight: '600', color: '#495057', margin: '0 0 8px 0', lineHeight: '1.3' }}>Player Lineups</h4>
                <p className="section-description mb-0" style={{ fontSize: '14px', color: '#6c757d', margin: '0', lineHeight: '1.4' }}>Manage players for this team</p>
              </div>
              
              {/* Right: Add Player Button */}
              <div className="filter-controls-right" style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'flex-end', background: 'transparent', backgroundColor: 'transparent' }}>
                <Button 
                  variant="secondary" 
                  onClick={() => setShowPlayerForm(true)}
                  className="add-team-btn"
                  style={{ 
                    position: 'relative', 
                    zIndex: 15,
                    backgroundColor: '#6c757d',
                    borderColor: '#6c757d',
                    borderRadius: '8px',
                    padding: '10px 20px',
                    fontSize: '14px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    minWidth: '120px',
                    justifyContent: 'center'
                  }}
                >
                  Add Player
                  <i className="ri-add-line" style={{ fontSize: '16px' }}></i>
                </Button>
              </div>
            </div>
          </div>

          <Row>
            <Col>
              {/* Player Form */}
              {showPlayerForm && (
                <Card className="player-form-card mb-4" style={{ margin: '0 15px' }}>
                  <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="mb-0">{editingPlayer ? 'Edit Player' : 'Add New Player'}</h5>
                      <Button 
                        variant="link" 
                        className="close-form-btn"
                        onClick={handleClosePlayerForm}
                      >
                        <i className="ri-close-line"></i>
                      </Button>
                    </div>

                    <PlayerForm
                      player={editingPlayer}
                      teamId={teamId}
                      teamName={team?.name || ''}
                      ageCategories={ageCategories}
                      teamAgeCategories={team?.ageGroups || []}
                      requireDocuments={true}
                      // No tournamentId prop - will use dynamic getCurrentActiveTournament()
                      onSubmit={async (playerData: PlayerFormData) => {
                        try {
                          setIsSubmitting(true);
                          setSubmitError(null);

                          if (editingPlayer) {
                            // Handle edit case - use update player API
                            const response = await updatePlayer(editingPlayer._id, {
                              name: playerData.name,
                              profilePic: playerData.profilePic,
                              dob: playerData.dob,
                              ageCategory: playerData.ageCategory,
                              parentName: playerData.parentName,
                              documents: playerData.documents || []
                            });

                            if (!response.success) {
                              throw new Error(response.message || 'Failed to update player');
                            }

                            setShowPlayerForm(false);
                            setEditingPlayer(null);
                            await loadPlayers();
                          } else {
                            // Handle add case - use the specified API endpoint
                            const response = await addPlayer(teamId, {
                              name: playerData.name,
                              profilePic: playerData.profilePic,
                              dob: playerData.dob,
                              ageCategory: playerData.ageCategory,
                              parentName: playerData.parentName,
                              documents: playerData.documents || []
                            });

                            if (!response.success) {
                              throw new Error(response.message || 'Failed to add player');
                            }

                            setShowPlayerForm(false);
                            setEditingPlayer(null);
                            await loadPlayers();
                          }
                        } catch (error: any) {
                          console.error('Failed to save player:', error);
                          setSubmitError(error.message || 'Failed to save player. Please try again.');
                        } finally {
                          setIsSubmitting(false);
                        }
                      }}
                      onCancel={handleClosePlayerForm}
                      isSubmitting={isSubmitting}
                      submitError={submitError}
                    />
                  </CardBody>
                </Card>
              )}

              {/* Players Table */}
              <Card style={{ margin: '0 15px' }}>
                <CardBody>
                  {players.length === 0 ? (
                    <div className="text-center py-5">
                      <i className="ri-user-line" style={{ fontSize: '3rem', color: '#6c757d' }}></i>
                      <h5 className="mt-3">No players found</h5>
                      <p className="text-muted">No players available. Add a new player to get started.</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <Table className="table">
                        <thead>
                          <tr>
                            <th>Profile</th>
                            <th>Name</th>
                            <th>DOB</th>
                            <th>Age Category</th>
                            <th>Verify Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {players.map((player) => (
                            <tr key={player._id}>
                              <td>
                                {player.profilePic ? (
                                  <img 
                                    src={getImageUrl(player.profilePic)}
                                    alt={player.name}
                                    className="player-avatar"
                                    style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }}
                                    onError={(e) => {
                                      const target = e.target as HTMLImageElement;
                                      target.style.display = 'none';
                                      target.nextElementSibling?.classList.remove('d-none');
                                    }}
                                  />
                                ) : null}
                                {!player.profilePic && (
                                  <div className="default-player-avatar">
                                    <i className="ri-user-line"></i>
                                  </div>
                                )}
                              </td>
                              <td>{player.name}</td>
                              <td>{player.dob}</td>
                              <td>{player.ageCategory}</td>
                              <td>
                                <Badge 
                                  bg={player.verificationStatus === 'approved' ? 'success' : 
                                      player.verificationStatus === 'referred_back' ? 'warning' : 'warning'}
                                >
                                  {player.verificationStatus === 'approved' ? 'Approved' : 
                                   player.verificationStatus === 'referred_back' ? 'Referred Back' : 'Pending'}
                                </Badge>
                              </td>
                              <td>
                                <div className="d-flex gap-2">
                                  <Button
                                    variant="link"
                                    size="sm"
                                    className="btn-edit p-0"
                                    onClick={() => handleEditPlayer(player)}
                                    title="Edit Player"
                                  >
                                    <i className="ri-edit-line"></i>
                                  </Button>
                                  <Button
                                    variant="link"
                                    size="sm"
                                    className="btn-delete p-0"
                                    onClick={() => handleDeletePlayer(player)}
                                    title="Delete Player"
                                  >
                                    <i className="ri-delete-bin-line"></i>
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </Container>

      {/* Delete Modal */}
      <DeleteModal
        show={showDeletePlayerModal}
        handleClose={closeDeleteModal}
        deleteModalFunction={confirmDeletePlayer}
        title="Delete Player"
        message={deletePlayerError || `Are you sure you want to delete "${deletePlayerTarget?.name}"? This action cannot be undone.`}
        confirmLabel={isDeletingPlayer ? "Deleting..." : "Yes, Delete"}
        cancelLabel="Cancel"
        isLoading={isDeletingPlayer}
        error={deletePlayerError}
      />
    </div>
  );
};

export default ClubPlayerManagement; 